import { Row } from '@tanstack/react-table';
import { Loader2, PlusIcon } from 'lucide-react';
import { ComponentProps } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../design-system/form/form-primitives';
import { Inline } from '../../../../design-system/inline/inline';
import { Select } from '../../../../design-system/select/select';
import { AccountView } from '../../../../types/entity-types';
import { cn } from '../../../../utils/cn';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';
import { AccountMappingFormValues } from './account-mapping-form';
import { XeroBankAccountView } from './account-mapping-table';

type XeroAccountIdFormFieldProps = {
  row: Row<AccountView>;
  xeroBankAccounts: XeroBankAccountView[];
  isCreatingXeroAccount: boolean;
  onCreateXeroAccount: () => Promise<void>;
};

export const XeroAccountIdFormField = ({
  row,
  xeroBankAccounts,
  isCreatingXeroAccount,
  onCreateXeroAccount,
}: XeroAccountIdFormFieldProps) => {
  const { control, watch, trigger } = useFormContext<AccountMappingFormValues>();

  const accountsValues = watch('accounts');
  const idx = accountsValues.findIndex((a) => a.accountId === row.original.id);

  const mappedXeroAccountIds = new Set(
    accountsValues
      .filter((account) => account.accountId !== row.original.id)
      .map((account) => account.xeroAccountId)
      .filter(Boolean),
  );

  const availableXeroBankAccounts = xeroBankAccounts.filter((account) => !mappedXeroAccountIds.has(account.id));

  if (idx === -1) {
    return null;
  }

  const fieldPath = `accounts.${idx}.xeroAccountId` as const;
  const placeholder = isCreatingXeroAccount ? 'Creating Xero Account...' : 'Select a Xero Account';

  return (
    <FormField
      control={control}
      name={fieldPath}
      render={({ field, fieldState }) => {
        return (
          <FormItem className="relative">
            <FormControl>
              <Select
                value={field.value}
                placeholder={placeholder}
                disabled={isCreatingXeroAccount}
                onValueChange={async (value) => {
                  if (value === 'create') {
                    await onCreateXeroAccount();

                    return;
                  }

                  field.onChange(value);
                }}
                onOpenChange={(open) => {
                  if (!open && !isCreatingXeroAccount) {
                    field.onBlur();

                    if (!field.value) {
                      trigger(fieldPath);
                    }
                  }
                }}
                triggerLabel={
                  <FormLabel className={cn('text-xs font-medium text-seapoint-gray', { hidden: true })}>
                    Xero Account
                  </FormLabel>
                }
                triggerClassName={cn(
                  'px-4 py-3 rounded-[10px] [&_.account-logo]:w-4 [&_.account-logo]:h-4 [&_.account-type]:hidden bg-white border border-seapoint-gray-border text-sm font-medium',
                  {
                    'border-seapoint-red': !!fieldState.error,
                    'text-black': !!field.value,
                    'text-seapoint-gray-text': !field.value,
                  },
                )}
              >
                {availableXeroBankAccounts.map((xeroBankAccount) => (
                  <SelectItemWrapper
                    key={xeroBankAccount.id}
                    xeroBankAccount={xeroBankAccount}
                    isCreatingXeroAccount={isCreatingXeroAccount}
                  />
                ))}
              </Select>
            </FormControl>

            <FormMessage className="absolute right-5 top-[0.5px] w-full text-right text-[10px] font-normal" />
          </FormItem>
        );
      }}
    />
  );
};

type SelectItemWrapperProps = {
  xeroBankAccount: XeroBankAccountView;
  isCreatingXeroAccount: ComponentProps<typeof XeroAccountIdFormField>['isCreatingXeroAccount'];
};

const SelectItemWrapper = ({ xeroBankAccount, isCreatingXeroAccount }: SelectItemWrapperProps) => {
  const { type, id, name } = xeroBankAccount;

  switch (type) {
    case 'create':
      return (
        <Select.Item value={id} className="border-t border-seapoint-gray-background-hover">
          <Inline space={1} className="items-center">
            {isCreatingXeroAccount ? <Loader2 size={12} className="animate-spin" /> : <PlusIcon size={12} />}
            {name}
          </Inline>
        </Select.Item>
      );
    case 'existing':
      return (
        <Select.Item value={id}>
          {isCreatingXeroAccount ? (
            <Inline space={1} className="items-center">
              <Loader2 size={12} className="animate-spin" />
              {name}
            </Inline>
          ) : (
            name
          )}
        </Select.Item>
      );
    default:
      return exhaustiveCheck(type);
  }
};
